import React, { useEffect, useState } from 'react';
import { getReporteMorosidad, getReporteIngresos, exportCarteraExcel, exportMorosidadExcel, exportPagosExcel } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell
} from 'recharts';
import { AlertTriangle, DollarSign, TrendingUp, Phone, Download, FileSpreadsheet } from 'lucide-react';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 0,
    }).format(value);
};

const COLORS = ['hsl(161, 91%, 16%)', 'hsl(160, 84%, 39%)', 'hsl(28, 87%, 37%)', 'hsl(0, 72%, 42%)'];

const nivelColors = {
    leve: 'bg-warning/10 text-warning border-warning/20',
    media: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    alta: 'bg-destructive/10 text-destructive border-destructive/20',
};

export default function ReportesPage() {
    const [morosidad, setMorosidad] = useState(null);
    const [ingresos, setIngresos] = useState(null);
    const [periodo, setPeriodo] = useState('mes');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadIngresos(periodo);
    }, [periodo]);

    const loadData = async () => {
        try {
            const [morosidadRes, ingresosRes] = await Promise.all([
                getReporteMorosidad(),
                getReporteIngresos('mes')
            ]);
            setMorosidad(morosidadRes.data);
            setIngresos(ingresosRes.data);
        } catch (error) {
            toast.error('Error al cargar reportes');
        } finally {
            setLoading(false);
        }
    };

    const loadIngresos = async (p) => {
        try {
            const { data } = await getReporteIngresos(p);
            setIngresos(data);
        } catch (error) {
            console.error('Error loading ingresos:', error);
        }
    };

    const downloadExcel = async (type) => {
        try {
            let response;
            let filename;
            
            if (type === 'cartera') {
                response = await exportCarteraExcel();
                filename = `cartera_${new Date().toISOString().split('T')[0]}.xlsx`;
            } else if (type === 'morosidad') {
                response = await exportMorosidadExcel();
                filename = `morosidad_${new Date().toISOString().split('T')[0]}.xlsx`;
            } else if (type === 'pagos') {
                response = await exportPagosExcel({});
                filename = `pagos_${new Date().toISOString().split('T')[0]}.xlsx`;
            }
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Excel descargado');
        } catch (error) {
            toast.error('Error al exportar');
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    const pieData = morosidad?.resumen?.por_nivel 
        ? [
            { name: 'Leve', value: morosidad.resumen.por_nivel.leve },
            { name: 'Media', value: morosidad.resumen.por_nivel.media },
            { name: 'Alta', value: morosidad.resumen.por_nivel.alta },
        ].filter(d => d.value > 0)
        : [];

    return (
        <div className="space-y-6" data-testid="reportes-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Reportes</h1>
                    <p className="text-muted-foreground mt-1">Análisis de cartera y rendimiento</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => downloadExcel('cartera')} data-testid="export-cartera">
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Cartera
                    </Button>
                    <Button variant="outline" onClick={() => downloadExcel('morosidad')} data-testid="export-morosidad">
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Morosidad
                    </Button>
                    <Button variant="outline" onClick={() => downloadExcel('pagos')} data-testid="export-pagos">
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Pagos
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="morosidad" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="morosidad" data-testid="tab-morosidad">Morosidad</TabsTrigger>
                    <TabsTrigger value="ingresos" data-testid="tab-ingresos">Ingresos</TabsTrigger>
                </TabsList>

                {/* Morosidad Tab */}
                <TabsContent value="morosidad" className="space-y-6">
                    {/* Resumen Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Card className="animate-fadeIn">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Total en Mora</p>
                                        <p className="text-2xl font-heading font-bold text-destructive">
                                            {formatCurrency(morosidad?.resumen?.mora_total || 0)}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-destructive/10">
                                        <AlertTriangle className="w-6 h-6 text-destructive" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="animate-fadeIn stagger-1">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Préstamos Morosos</p>
                                        <p className="text-2xl font-heading font-bold">
                                            {morosidad?.resumen?.total_morosos || 0}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-warning/10">
                                        <DollarSign className="w-6 h-6 text-warning" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="animate-fadeIn stagger-2">
                            <CardContent className="p-6">
                                <p className="text-sm text-muted-foreground mb-3">Por Nivel</p>
                                <div className="flex gap-2">
                                    <Badge variant="outline" className={nivelColors.leve}>
                                        Leve: {morosidad?.resumen?.por_nivel?.leve || 0}
                                    </Badge>
                                    <Badge variant="outline" className={nivelColors.media}>
                                        Media: {morosidad?.resumen?.por_nivel?.media || 0}
                                    </Badge>
                                    <Badge variant="outline" className={nivelColors.alta}>
                                        Alta: {morosidad?.resumen?.por_nivel?.alta || 0}
                                    </Badge>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Pie Chart */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-lg">Distribución por Nivel</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    {pieData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={90}
                                                    paddingAngle={4}
                                                    dataKey="value"
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index + 1]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-muted-foreground">
                                            Sin datos de morosidad
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Top Morosos */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-lg">Top 10 Morosos</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Cliente</TableHead>
                                            <TableHead>Mora</TableHead>
                                            <TableHead>Días</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {morosidad?.detalle?.slice(0, 10).map((item, i) => (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium">{item.cliente_nombre}</p>
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Phone className="w-3 h-3" />
                                                            {item.cliente_telefono}
                                                        </p>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-mono font-bold text-destructive">
                                                    {formatCurrency(item.mora_total)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={nivelColors[item.nivel_mora]}>
                                                        {item.dias_atraso}d
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!morosidad?.detalle || morosidad.detalle.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                                    No hay préstamos en mora
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Ingresos Tab */}
                <TabsContent value="ingresos" className="space-y-6">
                    <div className="flex justify-end">
                        <Select value={periodo} onValueChange={setPeriodo}>
                            <SelectTrigger className="w-40" data-testid="periodo-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="dia">Hoy</SelectItem>
                                <SelectItem value="semana">Semana</SelectItem>
                                <SelectItem value="mes">Mes</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Resumen */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="animate-fadeIn">
                            <CardContent className="p-6 text-center">
                                <p className="text-sm text-muted-foreground">Total</p>
                                <p className="text-2xl font-heading font-bold text-primary">
                                    {formatCurrency(ingresos?.resumen?.total || 0)}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="animate-fadeIn stagger-1">
                            <CardContent className="p-6 text-center">
                                <p className="text-sm text-muted-foreground">Capital</p>
                                <p className="text-2xl font-heading font-bold">
                                    {formatCurrency(ingresos?.resumen?.capital || 0)}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="animate-fadeIn stagger-2">
                            <CardContent className="p-6 text-center">
                                <p className="text-sm text-muted-foreground">Interés</p>
                                <p className="text-2xl font-heading font-bold text-accent">
                                    {formatCurrency(ingresos?.resumen?.interes || 0)}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="animate-fadeIn stagger-3">
                            <CardContent className="p-6 text-center">
                                <p className="text-sm text-muted-foreground">Mora</p>
                                <p className="text-2xl font-heading font-bold text-warning">
                                    {formatCurrency(ingresos?.resumen?.mora || 0)}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Ingresos por Día</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={ingresos?.por_dia || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 85%)" />
                                        <XAxis 
                                            dataKey="fecha" 
                                            tickFormatter={(val) => val.slice(5)}
                                            stroke="hsl(240, 4%, 46%)"
                                            fontSize={12}
                                        />
                                        <YAxis 
                                            tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
                                            stroke="hsl(240, 4%, 46%)"
                                            fontSize={12}
                                        />
                                        <Tooltip 
                                            formatter={(value, name) => [formatCurrency(value), 
                                                name === 'capital' ? 'Capital' : 
                                                name === 'interes' ? 'Interés' : 
                                                name === 'mora' ? 'Mora' : 'Total'
                                            ]}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid hsl(240, 6%, 85%)',
                                                borderRadius: '8px',
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="capital" fill={COLORS[0]} name="Capital" stackId="a" />
                                        <Bar dataKey="interes" fill={COLORS[1]} name="Interés" stackId="a" />
                                        <Bar dataKey="mora" fill={COLORS[2]} name="Mora" stackId="a" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
